import React, { useState, useEffect } from 'react';
import { Menu, X, Phone, Mail, ChevronDown, Shield, TrendingUp, Users, Award, Calendar } from 'lucide-react';
import { Button } from './ui/button';
import { useNavigate, useLocation } from 'react-router-dom';

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isTrainingDropdownOpen, setIsTrainingDropdownOpen] = useState(false);
  const [isMobileTrainingOpen, setIsMobileTrainingOpen] = useState(false);
  const [dropdownTimeout, setDropdownTimeout] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (dropdownTimeout) {
        clearTimeout(dropdownTimeout);
      }
    };
  }, [dropdownTimeout]);

  const handleMouseEnter = () => {
    if (dropdownTimeout) {
      clearTimeout(dropdownTimeout);
      setDropdownTimeout(null);
    }
    setIsTrainingDropdownOpen(true);
  };

  const handleMouseLeave = () => {
    const timeout = setTimeout(() => {
      setIsTrainingDropdownOpen(false);
    }, 300); // 300ms delay before closing
    setDropdownTimeout(timeout);
  };

  const trainingCategories = [
    {
      label: 'ISO Training Services',
      path: '/training/iso-training-services',
      icon: Shield,
      description: 'Lead, Internal & Awareness Training',
      color: 'text-blue-600'
    },
    {
      label: 'HEI Training',
      path: '/training/hei-training',
      icon: Award,
      description: 'Higher Education Institution Trainings',
      color: 'text-purple-600'
    },
    {
      label: 'Professional Development',
      path: '/training/professional-development-training-programs',
      icon: TrendingUp,
      description: 'Leadership & Skills Training',
      color: 'text-emerald-600'
    },
    {
      label: 'Industry Training',
      path: '/industry-training',
      icon: Users,
      description: 'Sector-Specific Solutions',
      color: 'text-orange-600'
    },
    {
      label: 'Training Calendar',
      path: '/training-calendar',
      icon: Calendar,
      description: 'Upcoming Training Schedule',
      color: 'text-gray-600',
      featured: true
    }
  ];

  const navLinks = [
    { label: 'Home', path: '/' },
    { label: 'Training', path: '/training-programs', hasDropdown: true },
    { label: 'Placement', path: '/placement' },
    { label: 'Gallery', path: '/gallery' },
    { label: 'About Us', path: '/about' },
    { label: 'Contact', path: '/contact' }
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-lg py-2' : 'bg-white py-3'
      }`}
    >
      <div className="container mx-auto px-4">
        {/* Top Bar - Contact Info */}
        <div className="hidden md:flex justify-between items-center text-sm pb-3 border-b border-gray-200">
          <div className="flex gap-6">
            <a href="tel:04449591335" className="flex items-center gap-2 text-gray-600 hover:text-blue-600 transition-colors">
              <Phone className="h-4 w-4" />
              <span>044 49591335</span>
            </a>
            <a href="mailto:training@isde.academy" className="flex items-center gap-2 text-gray-600 hover:text-blue-600 transition-colors">
              <Mail className="h-4 w-4" />
              <span>training@isde.academy</span>
            </a>
          </div>
          <div className="flex gap-4">
            <button onClick={() => navigate('/validate-certificate')} className="text-gray-600 hover:text-blue-600 transition-colors">
              Validate Certificate
            </button>
            <button onClick={() => navigate('/training-calendar')} className="text-gray-600 hover:text-blue-600 transition-colors">
              Training Calendar
            </button>
          </div>
        </div>

        {/* Main Navigation */}
        <div className="flex justify-between items-center pt-3">
          {/* Logo */}
          <div className="flex items-center">
            <button 
              onClick={() => navigate('/')} 
              className="flex items-center gap-4 transition-transform hover:scale-105 group"
            >
              <div className="bg-white p-2 rounded-lg shadow-sm">
                <img 
                  src="https://customer-assets.emergentagent.com/job_training-hub-107/artifacts/shpkz69x_400%20x%20400%20logo-16.png" 
                  alt="ISDE Academy Logo" 
                  className="h-16 w-auto"
                />
              </div>
              <div className="hidden sm:block">
                <div className="text-2xl font-bold text-gray-800 leading-tight">
                  ISDE <span className="text-blue-600">Academy</span>
                </div>
                <div className="text-xs text-gray-500 font-medium">Training Excellence Since 2015</div>
              </div>
            </button>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-6">
            {navLinks.map((link) => (
              link.hasDropdown ? (
                <div
                  key={link.label}
                  className="relative"
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                >
                  <button
                    onClick={() => navigate(link.path)}
                    className={`flex items-center gap-1 text-gray-700 hover:text-blue-600 transition-all font-medium px-3 py-2 rounded-lg relative ${
                      location.pathname.startsWith('/training') 
                        ? 'text-blue-600 font-semibold bg-blue-50' 
                        : 'hover:bg-gray-50'
                    }`}
                  >
                    {link.label}
                    <ChevronDown className={`h-4 w-4 transition-transform ${isTrainingDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {/* Dropdown Menu */}
                  {isTrainingDropdownOpen && (
                    <div className="absolute top-full left-0 mt-2 w-80 bg-white rounded-lg shadow-2xl border border-gray-100 py-2 animate-in fade-in slide-in-from-top-2">
                      {trainingCategories.map((category, index) => {
                        const IconComponent = category.icon;
                        return (
                          <button
                            key={category.label}
                            onClick={() => {
                              navigate(category.path);
                              setIsTrainingDropdownOpen(false);
                            }}
                            className={`w-full flex items-start gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-left ${
                              category.featured ? 'border-t border-gray-200 mt-2 pt-3 bg-blue-50/50' : ''
                            }`}
                          >
                            <IconComponent className={`h-5 w-5 mt-0.5 ${category.color}`} />
                            <div className="flex-1">
                              <div className={`font-semibold ${category.featured ? 'text-blue-600' : 'text-gray-800'}`}>
                                {category.label}
                              </div>
                              <div className="text-xs text-gray-500 mt-0.5">
                                {category.description}
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              ) : (
                <button
                  key={link.label}
                  onClick={() => navigate(link.path)}
                  className={`text-gray-700 hover:text-blue-600 transition-all font-medium px-3 py-2 rounded-lg relative ${
                    location.pathname === link.path 
                      ? 'text-blue-600 font-semibold bg-blue-50' 
                      : 'hover:bg-gray-50'
                  }`}
                >
                  {link.label}
                  {location.pathname === link.path && (
                    <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600"></span>
                  )}
                </button>
              )
            ))}
          </nav>

          {/* CTA Button */}
          <div className="flex items-center gap-4">
            <Button
              className="hidden md:flex bg-blue-600 hover:bg-blue-700 text-white transition-all hover:scale-105 shadow-md hover:shadow-lg px-6"
              onClick={() => navigate('/contact')}
            >
              Book a Consultation
            </Button>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden p-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-gray-200 shadow-lg">
          <nav className="container mx-auto px-4 py-4 flex flex-col gap-2">
            {/* Mobile Contact Info */}
            <div className="flex flex-col gap-2 pb-4 mb-4 border-b border-gray-200">
              <a href="tel:04449591335" className="flex items-center gap-2 text-gray-600 hover:text-blue-600 transition-colors py-2">
                <Phone className="h-4 w-4" />
                <span>044 49591335</span>
              </a>
              <a href="mailto:training@isde.academy" className="flex items-center gap-2 text-gray-600 hover:text-blue-600 transition-colors py-2">
                <Mail className="h-4 w-4" />
                <span>training@isde.academy</span>
              </a>
            </div>

            {navLinks.map((link) => (
              <div key={link.label}>
                {link.hasDropdown ? (
                  <div>
                    <button
                      onClick={() => setIsMobileTrainingOpen(!isMobileTrainingOpen)}
                      className="w-full flex items-center justify-between text-gray-700 hover:text-blue-600 transition-colors font-medium py-3 px-4 rounded-lg hover:bg-gray-50"
                    >
                      <span>{link.label}</span>
                      <ChevronDown className={`h-4 w-4 transition-transform ${isMobileTrainingOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {isMobileTrainingOpen && (
                      <div className="ml-4 mt-2 space-y-2">
                        {trainingCategories.map((category) => {
                          const IconComponent = category.icon;
                          return (
                            <button
                              key={category.label}
                              onClick={() => {
                                navigate(category.path);
                                setIsMobileMenuOpen(false);
                                setIsMobileTrainingOpen(false);
                              }}
                              className="w-full flex items-start gap-3 px-4 py-2 hover:bg-gray-50 rounded-lg text-left"
                            >
                              <IconComponent className={`h-4 w-4 mt-0.5 ${category.color}`} />
                              <div className="flex-1">
                                <div className="font-medium text-gray-800 text-sm">{category.label}</div>
                                <div className="text-xs text-gray-500">{category.description}</div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ) : (
                  <button
                    onClick={() => {
                      navigate(link.path);
                      setIsMobileMenuOpen(false);
                    }}
                    className={`w-full text-gray-700 hover:text-blue-600 transition-colors font-medium py-3 text-left px-4 rounded-lg ${
                      location.pathname === link.path ? 'text-blue-600 font-semibold bg-blue-50' : 'hover:bg-gray-50'
                    }`}
                  >
                    {link.label}
                  </button>
                )}
              </div>
            ))}
            <Button
              className="bg-blue-600 hover:bg-blue-700 text-white w-full mt-2"
              onClick={() => {
                setIsMobileMenuOpen(false);
                navigate('/contact');
              }}
            >
              Book a Consultation
            </Button>
          </nav>
        </div>
      )}
    </header>
  );
};
